import React from 'react';
import { ApolloProvider, gql } from '@apollo/client';
import { BrowserRouter } from 'react-router-dom';
import Raven from 'raven-js';
import { Helmet } from 'react-helmet';
import cookie from 'js-cookie';

import { paymentService } from 'global/services/paymentService/paymentService';

import { setDefaultLocale } from 'app/utils/date';

import configureRaven from 'app/configure-raven';
import { register as registerServiceWorker } from 'app/service-worker/initialize';
import { getMobileOperatingSystem, getAppType } from 'app/utils/browser';
import { ABTestDataProvider } from 'app/containers/contexts/abTest.context';
import { requestPermission } from './push-notifications';

import apolloClient from './apollo/client';
import { SAVE_PUSH_TOKEN } from './graphql/network/pushNotifications';

import PromoProvider from './containers/PromoProvider';
import DesignProvider from './containers/DesignProvider';

import LocaleProvder, { LOCALE_RU, LOCALE_EN } from './containers/LocaleProvider';
import { OverlayProvider } from './containers/contexts/overlay.context';
import { EnvDataSaverSPA } from './containers/EnvDataSaverSPA';
import { DataSaverUser } from './containers/DataSaverUser';

import AppRoutes from './AppRoutes';

import locales from './locales';

import './styles/variables/normalize.scss';
import { fragmentRegistry } from './apollo/fragmentRegistry';

import { AppDataAccessContainer } from './AppDataAccessContainer';
import { OfferOpener } from './components/OfferGuard';


configureRaven();
paymentService.configure();

const waitForFirebase = () => new Promise((resolve, reject) => {
    const startedAt = Date.now();

    const check = () => {
        if (window.firebase && window.firebase.apps && window.firebase.apps.length) {
            resolve(window.firebase);
            return;
        }

        if (Date.now() - startedAt > 10000) {
            reject(new Error('Firebase initialization timeout'));
            return;
        }

        setTimeout(check, 100);
    };

    check();
});

/* REGISTRY */

// ROOT_MENU_DISH_ITEM
fragmentRegistry.register(gql`
    fragment ROOT_MENU_DISH_ITEM on menuDish{
        id
        title
        titleEn
        caption
        captionEn
        cooking_time
        cook_until
        composition
        weight
        price
        portions
        previewImage
        previewMobileImage
        verticalImage
        umAlias
        categoryId: category_id
        category_description
        subcategory_id
        subcategory_name
        partnersComment
        parentId
        # BR-1104
        secondDishPart
        replaceableIngredient
        nutrition {
            carbohydrate {
                title
                value
                unit
            }
            fat {
                title
                value
                unit
            }
            protein {
                title
                value
                unit
            }
            calories {
                title
                value
                unit
            }
        }
        metaLabel @client
        tags {
            id
            erp_id
            title
            category_id
            category_name
        }
        upsell_dishes {
            id
            title
            name_upsell
            price
            portions
        }
    }
`);

// ROOT_CART_DISH_ITEM
fragmentRegistry.register(gql`
    fragment ROOT_CART_DISH_ITEM on cartItem {
        dish_id
        type
        portions
        price
        discount_price
        promoDiscountPrice
        is_promo
        has_crossed_out_price
    }
`);

// ROOT_CART_TOTAL
fragmentRegistry.register(gql`
    fragment ROOT_CART_TOTAL on cartTotal {
        # Легаси поля, от которых нужно отказаться
        total_price
        total_discount_price
        total_common_price
        delivery_price
        promocode_applied
        promocode_amount
        discount_amount
        static_discount_price
        static_common_price
        referral_amount
        # FRNT-2607
        # Новые поля, на которые нужно перейти и отказаться от использования полей выше
        dishesPrice
        deliveryPrice
        subscriptionDiscountAmount
        promocodeDiscountAmount
        specialDiscountAmount
        subscriptionPrice
        onlinePrice
        cashPrice
        cashbackReward
        cashback_amount
        referral_amount
        applied_discount_amount
    }
`);

// ROOT_CART_DISCOUNT_CONDITIONS
fragmentRegistry.register(gql`
    fragment ROOT_CART_DISCOUNT_CONDITIONS on DiscountConditions {
        promocode
        promocode_status
        isCertificate
        isPromoCodeActivated
        promoCodeApplyingErrorMessage
        isPromocodeCombineWithDiscount
        isPromocodeCombineWithAccount
        differenceBetweenPromocodeAndOrder
        promoCodeApplyingErrorMessage
        isPromoCodeExists
        isSubscriptionActivated
        deliveriesCount
        discountType
        discountValue
        discountDuration
    }
`);

/* -------------------------------------------------------------------------- */
export default class App extends React.Component {
    state = {
        nativeAppToken: null,
        isUserFetched: false,
        isAuthed: false,
        hasPushToken: null,
        isPushNotificationsSupported: true,
        locale: LOCALE_RU,
    };

    swRegistration = null;

    firebaseMessaging = null;

    messagingToken = null;

    lastSavedMessagingToken = null;

    isNativeAppTokenSaved = false;

    componentDidMount() {
        registerServiceWorker();

        this.initializeWebPushListener();
        this.initializeNativePushTokenSaving();

        const queryLocale = window.location.search.includes('lang=en') ? LOCALE_EN : LOCALE_RU;
        const sessionLocale = sessionStorage.getItem('lang');


        const locale = sessionLocale || queryLocale;

        sessionStorage.setItem('lang', locale);
        setDefaultLocale(locale);

        this.setState({ locale });
    }

    componentDidUpdate(prevProps, prevState) {
        const { nativeAppToken, isUserFetched } = this.state;

        if (isUserFetched && !prevState.isUserFetched) {
            if (this.messagingToken) {
                this.saveWebPushToken(this.messagingToken);
            }
            if (!this.isNativeAppTokenSaved && nativeAppToken) {
                this.saveNativeAppToken();
                this.isNativeAppTokenSaved = true;
            }
            return;
        }

        if (isUserFetched) {
            if (this.messagingToken && this.messagingToken !== this.lastSavedMessagingToken) {
                this.saveWebPushToken(this.messagingToken);
            }
            if (!this.isNativeAppTokenSaved && nativeAppToken) {
                this.saveNativeAppToken();
                this.isNativeAppTokenSaved = true;
            }
        }
    }

    getMessagingToken = () => {
        if (!this.firebaseMessaging) return;

        if (!('Notification' in window) || window.Notification.permission !== 'granted') {
            return;
        }

        const firebaseMessagingKey = process.env.FIREBASE_VAPID_KEY;

        if (!firebaseMessagingKey) {
            Raven.captureMessage('[Push]: VAPID key is missing');
            return;
        }

        if (typeof this.firebaseMessaging.usePublicVapidKey === 'function') {
            this.firebaseMessaging.usePublicVapidKey(firebaseMessagingKey);
        }

        this.firebaseMessaging.getToken({
            vapidKey: firebaseMessagingKey,
        }).then((currentToken) => {
            if (!currentToken) {
                Raven.captureMessage('[Push]: FCM token is empty');
                return;
            }

            this.messagingToken = currentToken;
            const { isUserFetched } = this.state;
            if (isUserFetched) {
                this.saveWebPushToken(currentToken);
            }
        }).catch((err) => {
            Raven.captureException(err);
        });
    };

    saveWebPushToken = (token) => {
        const { isUserFetched } = this.state;

        if (!token || !isUserFetched || this.lastSavedMessagingToken === token) return;

        if (!('Notification' in window) || window.Notification.permission !== 'granted') return;

        this.lastSavedMessagingToken = token;

        apolloClient.mutate({
            mutation: SAVE_PUSH_TOKEN,
            variables: { token },
        }).catch((err) => {
            this.lastSavedMessagingToken = null;
            Raven.captureException(err);
        });
    };

    showForegroundNotification = async (payload) => {
        try {
            const registration = this.swRegistration || await navigator.serviceWorker.ready;
            this.swRegistration = registration;

            const notification = payload.notification || {};
            const data = payload.data || {};
            const title = notification.title || data.title;

            if (!title) return;

            const clickAction = notification.click_action
                || data.click_action
                || (payload.fcmOptions && payload.fcmOptions.link);

            await registration.showNotification(title, {
                badge: notification.icon || data.icon,
                body: notification.body || data.body,
                data: {
                    click_action: clickAction,
                },
                icon: notification.icon || data.icon,
                requireInteraction: true,
            });
        } catch (e) {
            Raven.captureException(e);
        }
    };

    handleRequestNotificationPermission = () => {
        requestPermission().then((reg) => {
            if (!reg) {
                this.setState({
                    isPushNotificationsSupported: false,
                });
                return;
            }

            this.swRegistration = reg;
            this.getMessagingToken();
            this.setState({
                isPushNotificationsSupported: false,
            });
        }).catch((err) => {
            Raven.captureException(err);
            this.setState({
                isPushNotificationsSupported: false,
            });
        });
    };

    /*
        🤔🤔🤔🤔🤔🤔🤔🤔🤔🤔
        TODO: Понять, что за проблема
    */
    /* Возможно, проблема здесь */
    handleUserFetched = (userData = {}) => {
        const { isAuthed, hasPushToken } = userData;

        this.setState((state) => {
            const nextState = {};

            if (!state.isUserFetched) {
                nextState.isUserFetched = true;
            }
            if (typeof isAuthed === 'boolean' && state.isAuthed !== isAuthed) {
                nextState.isAuthed = isAuthed;
            }
            if (typeof hasPushToken === 'boolean' && state.hasPushToken !== hasPushToken) {
                nextState.hasPushToken = hasPushToken;
            }

            return Object.keys(nextState).length ? nextState : null;
        });
    };

    initializeWebPushListener = async () => {
        const os = getMobileOperatingSystem();
        const appType = getAppType();

        const isMobile = os.android || os.ios;
        if (!isMobile) {
            this.setState({
                isPushNotificationsSupported: false,
            });
            return;
        }

        if (os.ios && appType === 'ios_app') {
            this.setState({
                isPushNotificationsSupported: false,
            });
            return;
        }

        const isIosStandalone = window.navigator.standalone === true
            || window.matchMedia('(display-mode: standalone)').matches;

        if (os.ios && !isIosStandalone) {
            this.setState({
                isPushNotificationsSupported: false,
            });
            return;
        }

        if (!('Notification' in window)
            || !('serviceWorker' in navigator)
            || !window.isSecureContext) {
            this.setState({
                isPushNotificationsSupported: false,
            });
            return;
        }

        try {
            await waitForFirebase();

            if (typeof window.firebase.messaging !== 'function') {
                throw new Error('Firebase messaging is not available');
            }

            const messaging = window.firebase.messaging();
            this.firebaseMessaging = messaging;

            this.firebaseMessaging.onMessage((payload) => {
                this.showForegroundNotification(payload);
            });
            this.firebaseMessaging.onTokenRefresh(() => {
                this.getMessagingToken();
            });

            if (window.Notification.permission === 'granted') {
                navigator.serviceWorker.ready.then((swReg) => {
                    this.swRegistration = swReg;
                });
                this.setState({
                    isPushNotificationsSupported: true,
                });
                return;
            }

            if (window.Notification.permission === 'denied') {
                this.setState({
                    isPushNotificationsSupported: false,
                });
                return;
            }

            this.setState({
                isPushNotificationsSupported: true,
            });
        } catch (e) {
            Raven.captureException(e);
            this.setState({
                isPushNotificationsSupported: false,
            });
        }
    };

    initializeNativePushTokenSaving() {
        const os = getMobileOperatingSystem();
        const appType = getAppType();

        if (os.ios && appType === 'ios_app') {
            const setTokenToState = () => {
                const nativeTokenFromCookie = cookie.get('pushToken');
                const nativeTokenLegacy = localStorage.getItem('pushToken');
                const nativeToken = nativeTokenFromCookie || nativeTokenLegacy;
                if (nativeToken) {
                    this.setState({ nativeAppToken: nativeToken });
                }
                return nativeToken;
            };

            const timerId = setInterval(() => {
                const token = setTokenToState();
                if (token) {
                    clearInterval(timerId);
                }
            }, 5000);
        }
    }

    saveNativeAppToken() {
        const { nativeAppToken, isUserFetched } = this.state;

        if (nativeAppToken && isUserFetched) {
            apolloClient.mutate({
                mutation: SAVE_PUSH_TOKEN,
                variables: { token: nativeAppToken },
            }).catch((err) => {
                this.isNativeAppTokenSaved = false;
                Raven.captureException(err);
            });
        }
    }

    render() {
        const {
            isPushNotificationsSupported,
            locale,
            isAuthed,
            isUserFetched,
            hasPushToken,
        } = this.state;

        return (
            <ApolloProvider client={apolloClient}>
                <Helmet>
                    <link rel="canonical" href="https://elementaree.ru/menu/" />
                </Helmet>
                <LocaleProvder locales={locales} locale={locale}>
                    <OverlayProvider>
                        <BrowserRouter>
                            {/* STEP 1 */}
                            <AppDataAccessContainer
                                onUserFetched={this.handleUserFetched}
                            >
                                <EnvDataSaverSPA />
                                <DataSaverUser />
                                <OfferOpener isScalable={window.location.pathname.includes('profile')} />
                                <ABTestDataProvider>
                                    <DesignProvider>
                                        <PromoProvider
                                            isPushNotificationsSupported={isPushNotificationsSupported}
                                            onRequestPushPermission={this.handleRequestNotificationPermission}
                                            onUserFetched={this.handleUserFetched}
                                            isAuthed={isAuthed}
                                            isUserFetched={isUserFetched}
                                            hasPushToken={hasPushToken}
                                        >
                                            {(promoProps) => <AppRoutes promoProps={promoProps} />}
                                        </PromoProvider>
                                    </DesignProvider>
                                </ABTestDataProvider>
                            </AppDataAccessContainer>
                        </BrowserRouter>
                    </OverlayProvider>
                </LocaleProvder>
            </ApolloProvider>
        );
    }
}

/*
    resolveAuth => resolveLocation => resolveFilters => resolveBasket

    resolveAuth
    isUser Authed мы тут получаем user

    resolveLocation
    регион

    resolveFilters
    периуды и УДД

    resolveBasket
    берем basket либо пустой либо заполняем еври
*/
